import {
  Button,
  Chip,
  ChipGroup,
  Icon,
  InfoCircleHollowSVG,
  TooltipDialog,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";

import {
  filterAndTooltipContainerGradelevelCss,
  legendContentCss,
  subHeadingCss,
  tooltipDialogCss,
  tooltipIconCss,
} from "@/search/Langston/FilterPanel/filters/ReadingLevels/ReadingLevels.styles";
import { Grades } from "@/search/Langston/SearchPage/SearchPage.constants";
import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import useGetStudent from "@/users/v3/useGetStudent";

import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import {
  getGradeLevelChipCss,
  gradeLevelChipGroupCss,
} from "./GradeLevelChips.styles";

export const GradeLevelChips = () => {
  const {
    getters: { isDisabledFilters },
    handlers: { hasSearchParamValue, toggleSearchParamValue },
  } = useSearchPageContext();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  const { trackAction, getEventName } = useTracker();
  const { isStudent } = useGetUserProperties();
  const { data: student, isPending: isStudentPending } =
    useGetStudent(!!isStudent);
  const isElementaryStudent =
    isStudent && !isStudentPending && (student?.actual_grade ?? 6) < 6;

  const gradeLevelFilterOptions = LangstonParameterSchema.grade_levels.values;

  const isHighSchoolChip = (value: string) =>
    value === Grades.GRADE_9 ||
    value === Grades.GRADE_10 ||
    value === Grades.GRADE_11 ||
    value === Grades.GRADE_12;

  const handleFilterClick = (_: unknown, value: string) => {
    if (isDisabledFilters) {
      return;
    }

    trackAction({
      event_name: getEventName(
        objectTypes.content,
        actionPrefixes.initiate,
        actionNames.filter,
      ),
      event_type: eventTypes.click,
      dimension_properties: {
        description: `Filter search by grade ${value}`,
      },
    });

    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    toggleSearchParamValue(LangstonParameterSchema.grade_levels.key, value);
  };

  return (
    <>
      <fieldset css={filterAndTooltipContainerGradelevelCss}>
        <legend css={subHeadingCss}>
          <span css={legendContentCss}>
            <h3 css={subHeadingCss}>Grade level</h3>
            <TooltipDialog
              offset={8}
              placement="bottom-start"
              header="Grade level"
              body={
                <>
                  This filters the reading level of the content.
                  <br />
                  <br />
                  For example, an article about the summer olympics has 5 levels
                  for 3rd, 5th, 7th, 9th, and 12th grades.
                </>
              }
              buttonProps={{
                flavor: Button.flavors.incognito,
              }}
              ariaProps={{
                "aria-label": "Grade Lavel Filter Tooltip",
              }}
              __cssFor={tooltipDialogCss}
              buttonContents={
                <Icon
                  SvgComponent={InfoCircleHollowSVG}
                  __cssFor={tooltipIconCss}
                />
              }
            />
          </span>
        </legend>

        <ChipGroup
          groupName="gradeLevelFilter"
          __cssFor={gradeLevelChipGroupCss}
          onChipEvent={handleFilterClick}
          size={ChipGroup.chipSizes.small}
          chipType={ChipGroup.chipTypes.filter}
        >
          {gradeLevelFilterOptions.map(({ label, value }) => {
            if (isElementaryStudent && isHighSchoolChip(label)) return null;

            const isActive = hasSearchParamValue(
              LangstonParameterSchema.grade_levels.key,
              value,
            );

            return (
              <Chip
                key={value}
                value={value}
                text={label}
                active={isActive}
                disabled={isDisabledFilters}
                __cssFor={getGradeLevelChipCss(isActive)}
                dataProps={{
                  "data-qa-selector": `select-${label}-grade-search-filter`,
                  "data-tracker-model": objectTypes.content,
                  "data-testid": `select-${label}-grade-search-filter`,
                }}
              />
            );
          })}
        </ChipGroup>
      </fieldset>
    </>
  );
};
